import { useFieldArray, useFormContext } from 'react-hook-form';

import { ActiveDistrictSchool } from 'features/TalentMarketplace/TalentFiltersSidebar.types';
import { ATSVacanciesModalTestIds } from 'data-testids/ATS';
import { Checkbox, TableCell, TableRow, useTheme } from '@mui/material';
import { notFoundIndex } from './SchoolTable';
import { TextButton } from 'sharedComponents/Buttons';
import { VacancyIncrementor } from '../VacancyIncrementor';
import { VacancyTableRowProps } from 'features/VacanciesModal/types';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import auth from 'utils/auth';

export const VacancyTableRow: React.FC<VacancyTableRowProps> = ({
  school,
  fieldName,
  isChildRow,
}) => {
  const theme = useTheme();
  const { watch, control } = useFormContext();
  const schoolRoles = watch(fieldName) || [];
  const { append, remove, update } = useFieldArray({ control, name: fieldName });
  const rowShouldBeDisabled =
    auth.isAtsLiteDistrict() && schoolRoles.length >= 1 && school.id !== schoolRoles[0].school_id;

  const toggleSchool = (school: ActiveDistrictSchool) => {
    const existingIndex = schoolRoles.findIndex((role) => role.school_id === school.id);

    if (existingIndex === notFoundIndex) {
      append({
        school_id: school.id,
        name: school.name,
        ftes_total: 1,
        visible_to_school_admin: true,
      });
    } else {
      remove(existingIndex);
    }
  };

  const toggleVisibility = (school) => {
    const existingIndex = schoolRoles.findIndex((role) => role.school_id === school.id);

    if (existingIndex === notFoundIndex) {
      append({
        school_id: school.id,
        name: school.name,
        ftes_total: 1,
        visible_to_school_admin: true,
      });
    } else {
      const currentRole = schoolRoles[existingIndex];
      update(existingIndex, {
        ...currentRole,
        visible_to_school_admin: !currentRole.visible_to_school_admin,
      });
    }
  };

  const ensureSchoolSelected = (school) => {
    const existingIndex = schoolRoles.findIndex((role) => role.school_id === school.id);
    if (existingIndex === notFoundIndex) {
      append({
        school_id: school.id,
        name: school.name,
        ftes_total: 1,
        visible_to_school_admin: true,
      });
      return schoolRoles.length;
    }
    return existingIndex;
  };

  const getVisibilityText = (school) => {
    const schoolRole = schoolRoles.find((role) => role.school_id === school.id);

    if (!schoolRole) {
      return (
        <TextButton
          sx={{ color: theme.palette.gray.medium }}
          onClick={() => toggleVisibility(school)}
          disabled={rowShouldBeDisabled}
        >
          <VisibilityOffIcon sx={{ mr: 1, fill: theme.palette.gray.mediumLight }} /> Hidden
        </TextButton>
      );
    }

    if (schoolRole.visible_to_school_admin) {
      return (
        <TextButton
          sx={{ color: theme.palette.primary.dark }}
          onClick={() => toggleVisibility(school)}
          disabled={rowShouldBeDisabled}
        >
          <VisibilityIcon sx={{ mr: 1 }} /> Can view
        </TextButton>
      );
    } else {
      return (
        <TextButton
          sx={{ color: theme.palette.gray.medium }}
          onClick={() => toggleVisibility(school)}
          disabled={rowShouldBeDisabled}
        >
          <VisibilityOffIcon sx={{ mr: 1, fill: theme.palette.gray.mediumLight }} /> Hidden
        </TextButton>
      );
    }
  };

  const isRowChecked = schoolRoles.some(
    (selectedSchool) => selectedSchool?.school_id === school?.id
  );
  const currentIndex = schoolRoles.findIndex(
    (selectedSchool) => selectedSchool?.school_id === school?.id
  );

  return (
    <TableRow
      key={school.id}
      sx={{
        backgroundColor: isRowChecked
          ? theme.palette.primary.fill
          : rowShouldBeDisabled
            ? theme.palette.gray.lightest
            : 'white',
      }}
      data-testid={ATSVacanciesModalTestIds.VACANCY_SCHOOL_ROW}
    >
      {isChildRow && (
        <>
          <TableCell></TableCell>
          <TableCell>
            <Checkbox
              checked={isRowChecked}
              onChange={() => toggleSchool(school)}
              sx={{ padding: 0.5 }}
              data-testid={ATSVacanciesModalTestIds.INDIVIDUAL_SCHOOL_ROW_CHECKBOX}
              disabled={rowShouldBeDisabled}
            />
            {school.name}
          </TableCell>
        </>
      )}
      {!isChildRow && (
        <>
          <TableCell>
            <Checkbox
              checked={isRowChecked}
              onChange={() => toggleSchool(school)}
              sx={{ padding: 0.5 }}
              data-testid={ATSVacanciesModalTestIds.INDIVIDUAL_SCHOOL_ROW_CHECKBOX}
              disabled={rowShouldBeDisabled}
            />
          </TableCell>
          <TableCell>{school.name}</TableCell>
        </>
      )}
      {isRowChecked ? (
        <>
          <TableCell>
            <VacancyIncrementor
              field={
                currentIndex === notFoundIndex ? '' : `${fieldName}.${currentIndex}.ftes_total`
              }
              onBeforeChange={() => {
                const index = ensureSchoolSelected(school);
                return `${fieldName}.${index}.ftes_total`;
              }}
              isDisabled={rowShouldBeDisabled}
              type="school"
            />
          </TableCell>
          <TableCell data-testid={ATSVacanciesModalTestIds.ROW_VISIBILITY_TEXT}>
            {getVisibilityText(school)}
          </TableCell>
        </>
      ) : (
        <>
          <TableCell></TableCell>
          <TableCell></TableCell>
        </>
      )}
    </TableRow>
  );
};
